import {createContext, useCallback, useContext, useEffect, useState} from 'react';

const CART_STORAGE_KEY = 'SHOP_CART';

const readStoredCart = () => {
    try {
        const raw = localStorage.getItem(CART_STORAGE_KEY);
        const parsed = raw ? JSON.parse(raw) : [];
        return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
        return [];
    }
};

const CartContext = createContext({
    cartLines      : [],
    cartCount      : 0,
    isDrawerOpen   : false,
    addToCart      : () => {
    },
    updateQuantity : () => {
    },
    removeFromCart : () => {
    },
    clearCart      : () => {
    },
    openDrawer     : () => {
    },
    closeDrawer    : () => {
    }
});

export const CartProvider = (props) => {
    const [cartLines, setCartLines] = useState(readStoredCart);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);

    useEffect(() => {
        localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cartLines));
    }, [cartLines]);

    const addToCart = useCallback((line) => {
        setCartLines((prev) => {
            const existingIndex = prev.findIndex((existingLine) => existingLine.itemVariantId === line.itemVariantId);
            const maxQuantity = line.stockQuantity ?? Infinity;

            if (existingIndex === -1) {
                return [...prev, {...line, quantity : Math.min(line.quantity, maxQuantity)}];
            }

            return prev.map((existingLine, index) =>
                index === existingIndex
                    ? {...existingLine, ...line, quantity : Math.min(existingLine.quantity + line.quantity, maxQuantity)}
                    : existingLine
            );
        });
        setIsDrawerOpen(true);
    }, []);

    const updateQuantity = useCallback((itemVariantId, quantity) => {
        setCartLines((prev) => {
            if (quantity <= 0) {
                return prev.filter((line) => line.itemVariantId !== itemVariantId);
            }

            return prev.map((line) =>
                line.itemVariantId === itemVariantId
                    ? {...line, quantity : line.stockQuantity ? Math.min(quantity, line.stockQuantity) : quantity}
                    : line
            );
        });
    }, []);

    const removeFromCart = useCallback((itemVariantId) => {
        setCartLines((prev) => prev.filter((line) => line.itemVariantId !== itemVariantId));
    }, []);

    const clearCart = useCallback(() => {
        setCartLines([]);
    }, []);

    const openDrawer = useCallback(() => setIsDrawerOpen(true), []);
    const closeDrawer = useCallback(() => setIsDrawerOpen(false), []);

    const cartCount = cartLines.reduce((total, line) => total + line.quantity, 0);

    return (
        <CartContext.Provider value={{
            cartLines,
            cartCount,
            isDrawerOpen,
            addToCart,
            updateQuantity,
            removeFromCart,
            clearCart,
            openDrawer,
            closeDrawer
        }}>
            {props.children}
        </CartContext.Provider>
    );
};

export const useCartContext = () => useContext(CartContext);
